import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette, STAGE_BG_TEXTURE } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import { CHARACTERS, GROUP_NAME, type CharacterDefinition } from '@core/config/characters';

const MAX_CARD_WIDTH = 180;
const SIX_COLUMN_WIDTH = 1240;
const THREE_COLUMN_WIDTH = 820;

/**
 * Pantalla de seleccion de personaje. Muestra a las integrantes de LUMINOVA en una
 * grilla que se adapta al tamaño de pantalla; al tocar una, se guarda como personaje
 * del jugador y se resalta. El personaje elegido se usa luego en el juego.
 */
export class CharacterSelectScene extends BaseScene {
  private selectionFrames = new Map<string, Phaser.GameObjects.Graphics>();
  private cardWidth = MAX_CARD_WIDTH;
  private cardHeight = MAX_CARD_WIDTH * 1.33;

  public constructor() {
    super(SceneKeys.CharacterSelect);
  }

  public create(): void {
    this.initResponsive();
    this.selectionFrames = new Map();
    this.addBackground(STAGE_BG_TEXTURE);

    const { height } = this.scale;

    this.add
      .text(this.centerX, height * 0.09, `Elegí tu ${GROUP_NAME}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '44px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    this.renderCards();
    this.refreshSelection();

    new TextButton(this, {
      x: this.centerX,
      y: height - 56,
      label: 'Volver al menú',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }

  private renderCards(): void {
    const { width, height } = this.scale;
    const count = CHARACTERS.length;

    const columns = width >= SIX_COLUMN_WIDTH ? count : width >= THREE_COLUMN_WIDTH ? 3 : 2;
    const rows = Math.ceil(count / columns);
    const gap = Math.max(14, width * 0.018);

    this.cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.94 - (columns - 1) * gap) / columns);

    const gridTop = height * 0.18;
    const gridBottom = height - 110;
    const gridAreaHeight = Math.max(180, gridBottom - gridTop);
    this.cardHeight = Math.min(this.cardWidth * 1.33, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * this.cardHeight + (rows - 1) * gap;
    const startY = gridTop + (gridAreaHeight - totalHeight) / 2 + this.cardHeight / 2;

    CHARACTERS.forEach((character, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, count - row * columns);
      const rowWidth = cardsInRow * this.cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + this.cardWidth / 2;

      const x = startX + column * (this.cardWidth + gap);
      const y = startY + row * (this.cardHeight + gap);
      this.createCard(character, x, y);
    });
  }

  private createCard(character: CharacterDefinition, x: number, y: number): void {
    const width = this.cardWidth;
    const height = this.cardHeight;
    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(character.color, 0.9);
    background.fillRoundedRect(-width / 2, -height / 2, width, height, 20);

    const frame = this.add.graphics();
    this.selectionFrames.set(character.key, frame);

    const portrait = this.add.image(0, -height * 0.06, character.texture).setOrigin(0.5);
    portrait.setScale((height * 0.74) / portrait.height);

    const name = this.add
      .text(0, height / 2 - 24, character.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    container.add([background, frame, portrait, name]);
    container.setSize(width, height);
    container.setInteractive({ useHandCursor: true });

    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.05));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_UP, () => this.selectCharacter(character.key));
  }

  private selectCharacter(characterKey: string): void {
    this.audio.playClick();
    this.profile.setCharacter(characterKey);
    this.refreshSelection();
  }

  private refreshSelection(): void {
    const selected = this.profile.character;
    this.selectionFrames.forEach((frame, key) => {
      frame.clear();
      if (key === selected) {
        frame.lineStyle(8, Palette.highlight, 1);
        frame.strokeRoundedRect(
          -this.cardWidth / 2,
          -this.cardHeight / 2,
          this.cardWidth,
          this.cardHeight,
          20,
        );
      }
    });
  }
}
